import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { takeUntil } from 'rxjs/operators';

import { DragAndDropDocument, ReportTemplate, ReportToolbar } from '../../../models';
import {
  DragAndDropService,
  EmailService,
  FileService,
  FormlyService,
  ModalService,
  NotificationService,
  ReportAuditService,
} from '../../../services';
import { BaseComponent } from '../../base/base.component';
import { EmailModalComponent } from '../../email-modal/email-modal.component';
import { ReportStatuses } from '../../../constants';

@Component({
  selector: 'app-report-toolbar',
  templateUrl: './report-toolbar.component.html',
  styleUrl: './report-toolbar.component.scss',
})
export class ReportToolbarComponent extends BaseComponent implements OnInit {
  @ViewChild('reportContent') reportContent: ElementRef<HTMLDivElement>;

  @Input() upload = false;
  @Input() folderName = '';
  @Input() subFolderName = '';
  @Input() container = '';

  reports: ReportTemplate[] = [];
  model: ReportToolbar = { report: undefined };
  fields: FormlyFieldConfig[];
  selectedReport: ReportTemplate;

  private uploadDetails: DragAndDropDocument = {
    folderName: undefined,
    subFolderName: undefined,
    fileName: undefined,
    container: undefined,
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private formlyService: FormlyService,
    private modalService: ModalService,
    private emailService: EmailService,
    private reportAuditService: ReportAuditService,
    private notificationService: NotificationService,
    private fileService: FileService,
    private dragAndDropService: DragAndDropService,
  ) {
    super();
  }

  ngOnInit() {
    this.resetModel();
  }

  setReports(reports: ReportTemplate[], selectFirst = false) {
    this.reports = reports;
    if (selectFirst && reports.length > 0) {
      this.model.report = reports[0].report;
    }
    this.selectedReport = reports.find((x) => x.report == this.model.report);
    if (!this.selectedReport) {
      this.resetModel();
    }
    this.getFields();
  }

  private resetModel() {
    this.model = { report: undefined };
  }

  private email(suffix?: string, sellingCompany?: string, emailSubject?: string) {
    const { modal, component } = this.modalService.openMedium<EmailModalComponent>(EmailModalComponent);
    component.reportTemplate.email = this.selectedReport.email;
    component.reportTemplate.ccEmail = this.selectedReport.ccEmail;
    component.reportTemplate.emailSubject = `${emailSubject}-${suffix}`;
    component.reportTemplate.emailBody = this.selectedReport.emailBody ?? ' ';
    component.reportTemplate.emailContacts = this.selectedReport.emailContacts;
    component.reportTemplate.attachDocuments = this.selectedReport.attachDocuments;
    component.reportTemplate.type = this.selectedReport.type;
    component.reportTemplate.suffix = suffix;
    modal.result.then(
      async (model) => {
        const pdf = await this.fileService.getPdfOutput(this.reportContent.nativeElement, this.model.report, suffix);
        let ccEmail = model.ccEmail ?? ' ';
        if (model.emailContacts && model.emailContacts.length > 0) {
          model.emailContacts.forEach((x) => (ccEmail += x.email + ','));
          ccEmail = ccEmail.substring(0, ccEmail.length - 1);
        }
        this.emailService
          .emailReportWithSellingCompany(
            model.email,
            ccEmail,
            model.emailSubject,
            model.emailBody,
            pdf,
            sellingCompany,
            model.attachDocuments ?? false,
            model.type ?? ' ',
            model.suffix,
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.emailSuccess(this.model.report);
              this.reportAuditService
                .updateReportAuditDetails(this.selectedReport.type, this.selectedReport.suffix, ReportStatuses.emailed)
                .pipe(takeUntil(this.destroy$))
                .subscribe();
            },
            error: () => this.notificationService.emailFail(this.model.report),
          });
      },
      () => {},
    );
  }

  private async download(suffix?: string) {
    this.reportAuditService
      .updateReportAuditDetails(this.selectedReport.type, this.selectedReport.suffix, ReportStatuses.downloaded)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    await this.fileService.downloadPdf(this.reportContent.nativeElement, this.model.report, suffix);
  }

  private print() {
    this.reportAuditService
      .updateReportAuditDetails(this.selectedReport.type, this.selectedReport.suffix, ReportStatuses.printed)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
    const title = document.title;
    window.print();
    this.document.title = title;
  }

  private async uploadToBlob(suffix?: string) {
    const pdf = await this.fileService.getPdfOutput(this.reportContent.nativeElement, this.model.report, suffix);

    this.uploadDetails.folderName = this.folderName;
    this.uploadDetails.subFolderName = this.subFolderName;
    this.uploadDetails.fileName = `${this.model.report}${suffix ? '-' : ''}${suffix}.pdf`;
    this.uploadDetails.container = this.container;

    this.dragAndDropService.uploadPDF(this.uploadDetails, pdf).subscribe({
      next: () => this.notificationService.uploadSuccess(this.model.report),
      error: () => this.notificationService.uploadFail(this.model.report),
    });
  }

  private getFields() {
    this.fields = [
      this.formlyService.select(
        'report',
        '',
        this.reports.map((x: ReportTemplate) => ({ id: x.report, description: x.report })),
        undefined,
        {
          change: () => (this.selectedReport = this.reports.find((x) => x.report === this.model.report)),
          cssClass: 'report-select',
        },
      ),
      this.formlyService.button(
        $localize`Email`,
        () =>
          this.email(
            this.reports.find((x) => x.report === this.model.report).suffix,
            this.reports.find((x) => x.report === this.model.report).sellingCompany,
            this.reports.find((x) => x.report === this.model.report).report,
          ),
        { iconClass: 'fa fa-envelope', btnClass: 'success' },
        (field: FormlyFieldConfig) => !field.model?.report,
      ),
      this.formlyService.button(
        $localize`Download`,
        () => this.download(this.reports.find((x) => x.report === this.model.report).suffix),
        { iconClass: 'fa fa-file-download', btnClass: 'info text-white' },
        (field: FormlyFieldConfig) => !field.model?.report,
      ),
      this.formlyService.button(
        $localize`Print`,
        () => this.print(),
        { iconClass: 'fa fa-print' },
        (field: FormlyFieldConfig) => !field.model?.report,
      ),
      this.formlyService.button(
        $localize`Upload`,
        () => this.uploadToBlob(this.reports.find((x) => x.report === this.model.report).suffix),
        { iconClass: 'fa fa-upload', btnClass: 'secondary' },
        (field: FormlyFieldConfig) => !field.model?.report || !this.upload,
      ),
    ];
  }
}
